import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PackagePlus, Truck, ArrowRightLeft, Trash2, FileSpreadsheet, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/KpiCard";
import { StationTabs } from "@/components/StationTabs";
import { PeriodPicker, usePeriod } from "@/components/PeriodPicker";
import { MoveDialog, KINDS } from "@/components/adblue/MoveDialog";
import { SettingsDialog } from "@/components/adblue/SettingsDialog";
import { download } from "@/components/phones/PhoneRegister";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const L = (v) => `${Number(v || 0).toLocaleString("it-IT")} L`;
const fd = (d) => new Date(d).toLocaleDateString("it-IT");
const KIND_CLS = { arrival: "bg-emerald-50 text-emerald-800", refill: "bg-sky-50 text-sky-800", transfer: "bg-amber-50 text-amber-800" };

const StockCard = ({ s, onEdit }) => (
  <div className={`rounded-xl border bg-white p-4 ${s.stock <= s.default_liters ? "border-rose-300" : "border-slate-200"}`} data-testid={`adblue-stock-${s.station_id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{s.station_name}</div>
      <Button size="sm" variant="ghost" className="-mr-2 -mt-2 h-7 px-2" onClick={() => onEdit(s)} title="Magazzino iniziale e litri di default" data-testid={`adblue-settings-${s.station_id}`}><Settings2 className="h-4 w-4" /></Button>
    </div>
    <div className={`font-mono-num mt-1 text-3xl font-bold ${s.stock <= s.default_liters ? "text-rose-600" : "text-slate-900"}`} data-testid={`adblue-stock-value-${s.station_id}`}>{L(s.stock)}</div>
    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-slate-600">
      <span className="col-span-2 font-semibold uppercase tracking-wider text-[10px] text-slate-400">Nel periodo</span>
      <span>Arrivati: <b className="text-emerald-700" data-testid={`adblue-arrived-${s.station_id}`}>+{L(s.arrived)}</b></span><span>Riforniti: <b className="text-sky-700" data-testid={`adblue-refilled-${s.station_id}`}>−{L(s.refilled)}</b></span>
      <span>Trasferiti: <b className="text-amber-700" data-testid={`adblue-transferred-${s.station_id}`}>−{L(s.transferred)}</b></span><span>Iniziale: <b>{L(s.initial_liters)}</b></span>
      <span className="col-span-2">Default per furgone: <b>{L(s.default_liters)}</b></span>
    </div>
  </div>
);

export default function AdBluePage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [stations, setStations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stationId, setStationId] = useState("all");
  const [data, setData] = useState({ stations: [], items: [] });
  const [move, setMove] = useState(null);
  const [editing, setEditing] = useState(null);
  const [period, setPeriod] = usePeriod();
  const sid = isAdmin && stationId !== "all" ? stationId : undefined;
  const params = { station_id: sid, date_from: period.date_from || undefined, date_to: period.date_to || undefined };
  const load = useCallback(() => {
    api.get("/adblue", { params: { station_id: sid, date_from: period.date_from || undefined, date_to: period.date_to || undefined } }).then((r) => setData(r.data));
  }, [sid, period]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/stations").then((r) => setStations(r.data));
    api.get("/fleet/vehicles").then((r) => setVehicles(r.data.vehicles));
  }, []);
  const stockFor = useCallback((id) => (isAdmin ? data.stations.find((s) => s.station_id === id) : data.stations[0]), [data.stations, isAdmin]);
  const opStations = useMemo(() => (isAdmin ? stations : []), [isAdmin, stations]);
  const remove = async (i) => {
    if (!window.confirm(`Eliminare il movimento "${KINDS[i.kind]}" di ${L(i.liters)} del ${fd(i.move_date)}?`)) return;
    try { await api.delete(`/adblue/moves/${i.id}`); toast.success("Movimento eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const open = (kind) => setMove({ kind, station_id: sid || "" });

  return (
    <div className="fade-up">
      <PageHeader title="Magazzino AdBlue" subtitle="Arrivi, rifornimenti ai furgoni e trasferimenti, con giacenza per stazione.">
        <Button variant="outline" onClick={() => download("/adblue/export", params, "adblue.xlsx")} data-testid="adblue-export-button"><FileSpreadsheet className="mr-2 h-4 w-4" /> Scarica Excel</Button>
        <Button variant="outline" onClick={() => open("transfer")} data-testid="adblue-transfer-button"><ArrowRightLeft className="mr-2 h-4 w-4" /> Trasferimento</Button>
        <Button variant="outline" onClick={() => open("arrival")} data-testid="adblue-arrival-button"><PackagePlus className="mr-2 h-4 w-4" /> Arrivo AdBlue</Button>
        <Button onClick={() => open("refill")} data-testid="adblue-refill-button"><Truck className="mr-2 h-4 w-4" /> Rifornimento furgone</Button>
      </PageHeader>
      {isAdmin && <StationTabs stations={stations} value={stationId} onChange={setStationId} prefix="adblue" />}
      <PeriodPicker value={period} onChange={setPeriod} prefix="adblue" />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {data.stations.map((s) => <StockCard key={s.station_id} s={s} onEdit={setEditing} />)}
      </div>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="adblue-table">
            <thead className="border-b bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 [&_th]:whitespace-nowrap [&_th]:px-3 [&_th]:py-2">
              <tr><th>Data</th>{isAdmin && <th>Stazione</th>}<th>Movimento</th><th className="text-right">Litri</th><th>Furgone / Luogo</th><th>Note</th><th /></tr>
            </thead>
            <tbody className="divide-y">
              {data.items.map((i) => (
                <tr key={i.id} className="hover:bg-slate-50" data-testid={`adblue-row-${i.id}`}>
                  <td className="whitespace-nowrap px-3 py-2">{fd(i.move_date)}</td>
                  {isAdmin && <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">{i.station_name}</td>}
                  <td className="whitespace-nowrap px-3 py-2 text-xs"><span className={`rounded-full px-2 py-0.5 font-semibold ${KIND_CLS[i.kind]}`}>{KINDS[i.kind]}</span></td>
                  <td className="font-mono-num whitespace-nowrap px-3 py-2 text-right font-semibold">{i.kind === "arrival" ? "+" : "−"}{L(i.liters)}</td>
                  <td className="whitespace-nowrap px-3 py-2">{i.plate ? <span className="font-mono-num rounded-md border border-slate-300 px-2 py-0.5 text-xs font-semibold">{i.plate}</span> : i.place || "—"}</td>
                  <td className="min-w-[10rem] px-3 py-2 text-xs text-slate-600">{i.notes}</td>
                  <td className="px-3 py-2 text-right"><Button size="sm" variant="ghost" className="text-slate-400" onClick={() => remove(i)} data-testid={`adblue-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button></td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-500" data-testid="adblue-empty">Nessun movimento nel periodo. Il magazzino iniziale si imposta con l'icona ingranaggio.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <MoveDialog move={move} stations={opStations} stockFor={stockFor} vehicles={vehicles} isAdmin={isAdmin} onClose={() => setMove(null)} onSaved={() => { setMove(null); load(); }} />
      <SettingsDialog station={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
